import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import PublicChrome from './public/PublicChrome';
import { getImageProxyUrl } from '../utils/imageUtils';

// Utility to convert old proxy URLs to direct static URLs
const convertProxyToDirectUrl = (url: string): string => {
  if (url.includes('/api/image-proxy/blog/')) {
    const filename = url.split('/').pop();
    return `/uploads/images/blogs/${filename}`;
  }
  
  // Fix URLs with wrong ports (8547, 7419, etc.)
  if (url.includes('localhost:') && (url.includes(':8547') || url.includes(':7419') || url.includes(':3002'))) {
    // Extract just the path part after the port
    const match = url.match(/localhost:\d+(\/.*)/);
    if (match) {
      return match[1]; // Return just the path part
    }
  }
  
  return url;
};

interface Blog {
  _id: string;
  title: string;
  description: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  slug: string;
  isPublished: boolean;
  featured: boolean;
  readTime: number;
  seoTitle?: string;
  seoDescription?: string;
  views: number;
  likes: number;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

interface ServiceProject {
  _id: string;
  title: string;
  slug: string;
  description: string;
  imageUrl: string;
  category: string;
  network: string;
  tags: string[];
  featured: boolean;
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [allProjects, setAllProjects] = useState<ServiceProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [liked, setLiked] = useState(false);
  const [currentLikes, setCurrentLikes] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle like functionality
  const handleLike = async () => {
    if (!blog || liked) return;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      await axios.post(`${apiUrl}/api/blogs/${blog._id}/like`);
      setLiked(true);
      setCurrentLikes(prev => prev + 1);
    } catch (error) {
      console.error('Error liking blog:', error);
    }
  };

  // Process markdown content for display
  const processContent = (content: string) => {
    return content
      // Process images first (before other replacements) and convert proxy URLs to direct
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
        // Convert proxy URLs to direct static file URLs
        const finalSrc = convertProxyToDirectUrl(src);
        return `<img src="${finalSrc}" alt="${alt}" class="max-w-full h-auto rounded-lg shadow-lg my-6" style="max-height: 500px; object-fit: contain;" />`;
      })
      // Process text formatting
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-800 px-2 py-1 rounded text-sm">$1</code>')
      // Process headings
      .replace(/### (.*?)(\n|$)/g, '<h3 class="text-xl font-bold text-white mt-8 mb-4">$1</h3>')
      .replace(/## (.*?)(\n|$)/g, '<h2 class="text-2xl font-bold text-white mt-10 mb-6">$1</h2>')
      .replace(/# (.*?)(\n|$)/g, '<h1 class="text-3xl font-bold text-white mt-12 mb-8">$1</h1>')
      // Process lists
      .replace(/\n- (.*?)(\n|$)/g, '<ul class="list-disc list-inside text-gray-300 mb-4"><li>$1</li></ul>')
      .replace(/\n\d+\. (.*?)(\n|$)/g, '<ol class="list-decimal list-inside text-gray-300 mb-4"><li>$1</li></ol>')
      // Process quotes
      .replace(/\n> (.*?)(\n|$)/g, '<blockquote class="border-l-4 border-[var(--dusk-copper)] pl-4 italic text-gray-300 my-4">$1</blockquote>')
      // Process paragraphs
      .replace(/\n\n/g, '</p><p class="text-gray-300 mb-4 leading-relaxed">')
      .replace(/\n/g, '<br>');
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug]);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!slug) return;
      
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        
        // Fetch the blog post
        const blogResponse = await axios.get(`${apiUrl}/api/blogs/slug/${slug}`);
        if (blogResponse.data.success) {
          setBlog(blogResponse.data.data);
          setCurrentLikes(blogResponse.data.data.likes);
          
          // Increment view count
          await axios.post(`${apiUrl}/api/blogs/${blogResponse.data.data._id}/view`);
        }

        // Fetch all projects for services modal
        const allResponse = await axios.get(`${apiUrl}/api/projects?status=active&isPublished=true`);
        if (allResponse.data.success) {
          setAllProjects(allResponse.data.data.projects);
        }
      } catch (error) {
        console.error('Error fetching blog:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  // Set page title and meta description
  useEffect(() => {
    if (blog) {
      document.title = blog.seoTitle || blog.title;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', blog.seoDescription || blog.description || '');
      }
    }
    
    return () => {
      document.title = 'DUSKPROOF';
    };
  }, [blog]);

  // Handle click outside to close share menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showShareMenu && !target.closest('[data-share-menu]')) {
        setShowShareMenu(false);
      }
    };

    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);

  if (loading) {
    return (
      <PublicChrome>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--dusk-copper)]"></div>
        </div>
      </PublicChrome>
    );
  }

  if (!blog) {
    return (
      <PublicChrome>
        <section className="max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="animate-fade-in-up">
            <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight text-white">
              Blog Post Not Found
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
              The blog post you're looking for doesn't exist or has been removed.
            </p>
            <Link 
              to="/blog" 
              className="inline-block bg-[var(--dusk-copper)] hover:bg-[var(--dusk-amber)] text-white font-medium py-3 px-8 rounded-lg transition-colors shadow-md"
            >
              в†ђ Back to Blog
            </Link>
          </div>
        </section>
      </PublicChrome>
    );
  }

  return (
    <PublicChrome>
      <article className="max-w-4xl mx-auto px-6 py-20">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <span>/</span>
            <span className="text-gray-300">{blog.title}</span>
          </div>
        </nav>

        {/* Header */}
        <header className="mb-12">
          {/* Category */}
          <div className="mb-5">
            <span className="text-[0.72rem] font-semibold tracking-[0.14em] uppercase text-[var(--dusk-copper)]">
              {blog.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {blog.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[var(--dusk-muted)] mb-8">
            <span>{blog.author}</span>
            <span className="text-[var(--dusk-faint)]">·</span>
            <span>{formatDate(blog.createdAt)}</span>
            <span className="text-[var(--dusk-faint)]">·</span>
            <span>{blog.readTime} min read</span>
            <span className="text-[var(--dusk-faint)]">·</span>
            <span>{blog.views} views</span>
          </div>

          {/* Description — lead text, no box */}
          {blog.description && (
            <p className="text-lg md:text-xl text-[var(--dusk-muted)] leading-[1.7] mb-10 max-w-3xl border-l-2 border-[var(--dusk-copper)] pl-5 m-0">
              {blog.description}
            </p>
          )}

          {/* Featured Image */}
          {blog.imageUrl && (
            <div className="mb-8">
              <img 
                src={getImageProxyUrl(convertProxyToDirectUrl(blog.imageUrl))} 
                alt={blog.title}
                className="w-full h-64 md:h-96 object-cover rounded-lg shadow-2xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  // Hide if image fails to load (direct static serving)
                  target.style.display = 'none';
                }}
              />
            </div>
          )}
        </header>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <div 
            className="text-gray-300 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: `<p class="text-gray-300 mb-4 leading-relaxed">${processContent(blog.content)}</p>`
            }}
          />
        </div>

        {/* Tags + actions */}
        <footer className="mt-14 pt-8 border-t border-[var(--dusk-line)]">
          {blog.tags && blog.tags.length > 0 && (
            <p className="text-sm text-[var(--dusk-muted)] m-0 mb-8 leading-relaxed">
              <span className="text-[0.65rem] font-semibold tracking-[0.14em] uppercase text-[var(--dusk-faint)] mr-3">
                Tags
              </span>
              {blog.tags.map((tag, index) => (
                <span key={index}>
                  <span className="text-[var(--dusk-ink)]">{tag}</span>
                  {index < blog.tags.length - 1 ? (
                    <span className="text-[var(--dusk-faint)] mx-2">·</span>
                  ) : null}
                </span>
              ))}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6 text-sm">
              <button
                type="button"
                onClick={handleLike}
                disabled={liked}
                className={`bg-transparent p-0 border-0 cursor-pointer transition-colors ${
                  liked
                    ? 'text-[var(--dusk-copper)] cursor-not-allowed'
                    : 'text-[var(--dusk-muted)] hover:text-[var(--dusk-ink)]'
                }`}
              >
                {liked ? 'Liked' : 'Like'} · {currentLikes}
              </button>

              <div className="relative" data-share-menu>
                <button
                  type="button"
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="bg-transparent p-0 border-0 cursor-pointer text-[var(--dusk-muted)] hover:text-[var(--dusk-ink)] transition-colors"
                >
                  Share
                </button>

                {showShareMenu && (
                  <div className="absolute bottom-full mb-2 left-0 min-w-[180px] bg-[var(--dusk-bg)] border border-[var(--dusk-line)] z-50 py-1">
                    {[
                      {
                        label: 'X (Twitter)',
                        href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(window.location.href)}`,
                      },
                      {
                        label: 'Facebook',
                        href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
                      },
                      {
                        label: 'LinkedIn',
                        href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
                      },
                      {
                        label: 'Telegram',
                        href: `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`,
                      },
                    ].map((item) => (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => {
                          window.open(item.href, '_blank');
                          setShowShareMenu(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-[var(--dusk-muted)] hover:text-[var(--dusk-ink)] hover:bg-[rgba(208,0,104,0.08)] bg-transparent border-0 cursor-pointer"
                      >
                        {item.label}
                      </button>
                    ))}
                    <div className="border-t border-[var(--dusk-line)] my-1" />
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(window.location.href);
                          setCopySuccess(true);
                          setTimeout(() => setCopySuccess(false), 2000);
                        } catch (err) {
                          console.error('Failed to copy:', err);
                        }
                        setShowShareMenu(false);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-[var(--dusk-muted)] hover:text-[var(--dusk-ink)] hover:bg-[rgba(208,0,104,0.08)] bg-transparent border-0 cursor-pointer"
                    >
                      {copySuccess ? 'Copied!' : 'Copy link'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <Link
              to="/blog"
              className="text-sm text-[var(--dusk-copper)] no-underline hover:text-[var(--dusk-amber)] transition-colors"
            >
              ← Back to Blog
            </Link>
          </div>
        </footer>
      </article>
    </PublicChrome>
  );
};

export default BlogPost;

